import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import MainSidebar from "components/MainSidebar";
import CandlestickChart from "./components/CandlestickChart";
import CompanyFundamentals from "./components/CompanyFundamentals";
import DividendInfo from "./components/DividendInfo";
import OrderBook from "./components/OrderBook";
import OrderPanel from "./components/OrderPanel";
import PriceHeader from "./components/PriceHeader";
import TradeHistory from "./components/TradeHistory";

/* ── MOCK DATA ── */
const MOCK_STOCKS = [
  { id: "NVXC", symbol: "NVXC", name: "Nexus Virtual Corp", exchange: "NSX", sector: "Technology" },
  { id: "BLKM", symbol: "BLKM", name: "BlackMarket Holdings", exchange: "NSX", sector: "Black Market" },
  { id: "RETX", symbol: "RETX", name: "RetailX Industries", exchange: "NSX", sector: "Retail" },
  { id: "MFGP", symbol: "MFGP", name: "MegaFab Group", exchange: "NSX", sector: "Manufacturing" },
  { id: "FINV", symbol: "FINV", name: "FinVault Capital", exchange: "NSX", sector: "Financial" },
];

const MOCK_STOCK_DETAIL = {
  symbol: "NVXC",
  name: "Nexus Virtual Corp",
  exchange: "NSX",
  sector: "Technology",
  price: 142.58,
  change: 3.24,
  changePct: 2.32,
  volume: 1482300,
  open: 139.80,
  dayHigh: 144.20,
  dayLow: 138.95,
  prevClose: 139.34,
  bid: 142.55,
  ask: 142.61,
  marketCap: "$14.2B",
  revenue: "$3.8B",
  profitMargin: "22.4%",
  peRatio: "38.2x",
  eps: "$3.73",
  high52w: "$168.40",
  low52w: "$98.15",
  avgVolume: "1.1M",
  description: "Nexus Virtual Corp is a leading technology conglomerate operating within the Nexus Economic Terminal. The company specializes in virtual infrastructure, AI-driven trading algorithms, and digital asset management. Founded in Year 1 of the simulation, NVXC has grown to become the largest technology company by market capitalization on the NSX exchange.",
};

const MOCK_DIVIDENDS = {
  yield: "2.8%",
  perShare: "$3.99",
  payoutRatio: "32%",
  frequency: "Quarterly",
  schedule: [
    { date: "03/15/2026", type: "Q1 2026", amount: "$0.9975", paid: false },
    { date: "12/15/2025", type: "Q4 2025", amount: "$0.9975", paid: true },
    { date: "09/15/2025", type: "Q3 2025", amount: "$0.9975", paid: true },
    { date: "06/15/2025", type: "Q2 2025", amount: "$0.9975", paid: true },
    { date: "03/15/2025", type: "Q1 2025", amount: "$0.9975", paid: true },
  ],
};

const generateOrderBook = (basePrice) => ({
  asks: Array.from({ length: 8 }, (_, i) => ({
    price: +(basePrice + (i + 1) * 0.05)?.toFixed(2),
    size: Math.floor(Math.random() * 5000 + 500),
  })),
  bids: Array.from({ length: 8 }, (_, i) => ({
    price: +(basePrice - (i + 1) * 0.05)?.toFixed(2),
    size: Math.floor(Math.random() * 5000 + 500),
  })),
});

const generateTrades = () => {
  const players = ["Viper_X", "CryptoKing", "ShadowTrader", "NexusWolf", "IronFist", "GoldRush", "DarkHorse", "MarketMaker"];
  const now = Date.now();
  return Array.from({ length: 20 }, (_, i) => ({
    time: new Date(now - i * 47000)?.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
    side: Math.random() > 0.5 ? "BUY" : "SELL",
    price: +(142.58 + (Math.random() - 0.5) * 2)?.toFixed(2),
    quantity: Math.floor(Math.random() * 500 + 50),
    player: players?.[Math.floor(Math.random() * players?.length)],
  }));
};

const USER_STATS = {
  username: "Commander",
  level: 24,
  energy: 68,
  maxEnergy: 100,
  xp: 48200,
  xpToNext: 55000,
};

const NAV_ROUTES = [
  { label: "Dashboard", path: "/dashboard", icon: "LayoutDashboard" },
  { label: "Exchange", path: "/stock-exchange", icon: "BarChart2" },
  { label: "Stock Detail", path: "/stock-detail", icon: "TrendingUp" },
  { label: "Portfolio", path: "/portfolio", icon: "PieChart" },
  { label: "Businesses", path: "/businesses", icon: "Building2" },
  { label: "Crime Hub", path: "/crime-hub", icon: "Skull" },
];

/* ── COMPONENT ── */
const StockDetail = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState("NVXC");
  const [stock, setStock] = useState(MOCK_STOCK_DETAIL);
  const [orderBook, setOrderBook] = useState(() => generateOrderBook(MOCK_STOCK_DETAIL?.price));
  const [trades, setTrades] = useState(() => generateTrades());
  const [showOrderPanel, setShowOrderPanel] = useState(false); // mobile bottom sheet
  const [isMobile, setIsMobile] = useState(false);
  const [orderNotification, setOrderNotification] = useState(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Simulate tick updates
  useEffect(() => {
    const interval = setInterval(() => {
      setStock(prev => {
        const delta = (Math.random() - 0.49) * 0.8;
        const newPrice = Math.max(50, prev?.price + delta);
        const change = newPrice - prev?.prevClose;
        return { ...prev, price: +newPrice?.toFixed(2), change: +change?.toFixed(2), changePct: +((change / prev?.prevClose) * 100)?.toFixed(2) };
      });
      setOrderBook(prev => generateOrderBook(stock?.price));
    }, 5000);
    return () => clearInterval(interval);
  }, [stock?.price]);

  const handleOrderSubmit = (order) => {
    setOrderNotification(`${order?.side} order for ${order?.quantity} ${stock?.symbol} @ $${order?.price?.toFixed(2)} submitted!`);
    setTimeout(() => setOrderNotification(null), 4000);
    if (isMobile) setShowOrderPanel(false);
  };

  const sidebarWidth = isMobile ? 0 : sidebarCollapsed ? 72 : 280;

  return (
    <div className="min-h-screen" style={{ background: "var(--color-background)" }}>
      {/* Sidebar */}
      <MainSidebar
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(v => !v)}
        userRole="player"
        userStats={USER_STATS}
        notificationCounts={{ notifications: 3, wars: 1 }}
      />
      {/* Main Content */}
      <div className="transition-all duration-250" style={{ marginLeft: isMobile ? 0 : sidebarWidth, paddingBottom: isMobile ? 80 : 0 }}>
        {/* Top Bar */}
        <div className="sticky top-0 z-50 border-b border-border px-4 md:px-6 lg:px-8 py-3 flex items-center justify-between gap-3"
          style={{ background: "var(--color-secondary)" }}>
          <div className="flex items-center gap-3 min-w-0">
            {isMobile && <div className="w-8" />}
            {/* Breadcrumb */}
            <nav className="flex items-center gap-1.5 text-xs font-caption text-muted-foreground">
              <button onClick={() => navigate("/dashboard")} className="hover:text-primary transition-colors duration-150">Dashboard</button>
              <Icon name="ChevronRight" size={12} color="var(--color-muted-foreground)" />
              <button onClick={() => navigate("/stock-exchange")} className="hover:text-primary transition-colors duration-150">Exchange</button>
              <Icon name="ChevronRight" size={12} color="var(--color-muted-foreground)" />
              <span style={{ color: "var(--color-primary)" }}>{selectedSymbol}</span>
            </nav>
          </div>

          {/* Symbol Switcher */}
          <div className="flex items-center gap-1.5 overflow-x-auto">
            {MOCK_STOCKS?.map(s => (
              <button key={s?.id} onClick={() => setSelectedSymbol(s?.symbol)}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-mono font-medium whitespace-nowrap transition-all duration-150 flex-shrink-0 ${selectedSymbol === s?.symbol ? "border" : "border border-transparent hover:border-border"}`}
                style={selectedSymbol === s?.symbol
                  ? { background: "rgba(0,217,255,0.1)", color: "var(--color-primary)", borderColor: "rgba(0,217,255,0.3)" }
                  : { color: "var(--color-muted-foreground)" }}>
                {s?.symbol}
              </button>
            ))}
          </div>

          {/* Mobile Trade Button */}
          {isMobile && (
            <Button variant="default" size="sm" onClick={() => setShowOrderPanel(true)} iconName="Zap" iconPosition="left">
              Trade
            </Button>
          )}
        </div>

        {/* Order Notification */}
        {orderNotification && (
          <div className="mx-4 md:mx-6 lg:mx-8 mt-3 p-3 rounded-lg border flex items-center gap-2 text-sm font-caption"
            style={{ background: "rgba(16,185,129,0.1)", borderColor: "rgba(16,185,129,0.3)", color: "var(--color-success)" }}>
            <Icon name="CheckCircle" size={16} color="var(--color-success)" />
            {orderNotification}
          </div>
        )}

        {/* Page Content */}
        <div className="px-4 md:px-6 lg:px-8 py-4 md:py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5 lg:gap-6">

            {/* Left Column: Chart + Fundamentals + Trade History */}
            <div className="lg:col-span-2 space-y-4 md:space-y-5">
              <PriceHeader stock={stock} />
              <CandlestickChart symbol={stock?.symbol} />
              <CompanyFundamentals stock={stock} />
              <DividendInfo dividends={MOCK_DIVIDENDS} />
              <TradeHistory trades={trades} />
            </div>

            {/* Right Column: Order Panel + Order Book */}
            <div className="hidden lg:flex flex-col gap-4 md:gap-5">
              <OrderPanel stock={stock} onOrderSubmit={handleOrderSubmit} />
              <OrderBook orderBook={orderBook} currentPrice={stock?.price} />
            </div>
          </div>

          {/* Mobile: Order Book below */}
          <div className="lg:hidden mt-4">
            <OrderBook orderBook={orderBook} currentPrice={stock?.price} />
          </div>
        </div>
      </div>
      {/* Mobile Bottom Sheet: Order Panel */}
      {isMobile && showOrderPanel && (
        <>
          <div className="fixed inset-0 z-40 bg-black/80" onClick={() => setShowOrderPanel(false)} />
          <div className="fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl border-t border-border overflow-y-auto"
            style={{ background: "var(--color-card)", maxHeight: "85vh" }}>
            <div className="flex items-center justify-between p-4 border-b border-border">
              <span className="font-heading text-sm font-semibold text-foreground">Place Order — {stock?.symbol}</span>
              <button onClick={() => setShowOrderPanel(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-primary/10 transition-colors duration-150">
                <Icon name="X" size={16} color="var(--color-muted-foreground)" />
              </button>
            </div>
            <div className="p-4">
              <OrderPanel stock={stock} onOrderSubmit={handleOrderSubmit} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default StockDetail;